/* Dutch Car Care — interactions */
(() => {
  const root = document.documentElement;
  root.classList.add('dcc-js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Header state
  const header = document.querySelector('.dcc-header');
  const fab = document.querySelector('.dcc-fab');
  const onScroll = () => {
    const y = window.scrollY;
    header?.classList.toggle('is-scrolled', y > 8);
    fab?.classList.toggle('is-visible', y > 320 || !document.querySelector('.dcc-hero'));
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Mobile drawer
  const burger = document.querySelector('.dcc-burger');
  const drawer = document.getElementById('dcc-drawer');
  const setDrawer = (open) => {
    if (!burger || !drawer) return;
    burger.setAttribute('aria-expanded', String(open));
    burger.setAttribute('aria-label', open ? 'Menu sluiten' : 'Menu openen');
    drawer.classList.toggle('is-open', open);
    drawer.inert = !open;
    document.body.classList.toggle('dcc-lock', open);
  };
  if (drawer) drawer.inert = true;
  burger?.addEventListener('click', () => setDrawer(burger.getAttribute('aria-expanded') !== 'true'));
  drawer?.addEventListener('click', (e) => { if (e.target.closest('a')) setDrawer(false); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setDrawer(false); });

  // Scroll reveal
  const reveal = (scope) => {
    const els = scope.querySelectorAll('[data-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduced) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  };
  reveal(document);

  // 3D tilt + glow
  const tilt = (scope) => {
    if (!fine || reduced) return;
    scope.querySelectorAll('[data-tilt]').forEach((el) => {
      if (el.dataset.tiltReady) return;
      el.dataset.tiltReady = '1';
      const max = parseFloat(el.dataset.tilt) || 6;
      let raf = 0;
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.classList.add('is-tilting');
          el.style.setProperty('--ry', `${(px - 0.5) * max * 2}deg`);
          el.style.setProperty('--rx', `${(0.5 - py) * max * 2}deg`);
          el.style.setProperty('--mx', `${px * 100}%`);
          el.style.setProperty('--my', `${py * 100}%`);
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        el.classList.remove('is-tilting');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  };
  tilt(document);

  // Parallax
  let para = [...document.querySelectorAll('[data-parallax]')];
  if (!reduced) {
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      para.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const speed = parseFloat(el.dataset.parallax) || 0.08;
        const offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.setProperty('--py', `${offset.toFixed(1)}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
    document.addEventListener('shopify:section:load', () => { para = [...document.querySelectorAll('[data-parallax]')]; update(); });
  }

  // Theme editor: re-initialise freshly rendered sections
  document.addEventListener('shopify:section:load', (e) => { reveal(e.target); tilt(e.target); initBooking(e.target); });

  // 3D hero — load the heavy module only when the stage is near the viewport
  const stage = document.querySelector('[data-dcc-3d]');
  if (stage) {
    const start = () => import(stage.dataset.module).then((m) => m.mount(stage)).catch((err) => {
      console.warn('[DCC] 3D kon niet laden', err);
      stage.classList.add('is-fallback');
    });
    const webgl = (() => { try { const c = document.createElement('canvas'); return !!(c.getContext('webgl2') || c.getContext('webgl')); } catch (e) { return false; } })();
    const loadBtn = stage.querySelector('[data-3d-load]');
    if (!webgl || !stage.dataset.model) {
      stage.classList.add('is-fallback');
    } else if (window.innerWidth < 760 && loadBtn && !(navigator.connection && navigator.connection.effectiveType === '4g' && !navigator.connection.saveData)) {
      // Save mobile data: load the model on request
      stage.classList.add('is-ondemand');
      loadBtn.addEventListener('click', () => { stage.classList.remove('is-ondemand'); start(); }, { once: true });
    } else if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((en) => {
        if (en[0].isIntersecting) { io.disconnect(); (window.requestIdleCallback || setTimeout)(start, { timeout: 1200 }); }
      }, { rootMargin: '200px' });
      io.observe(stage);
    } else start();
  }

  // Booking configurator → WhatsApp
  function initBooking(scope) {
    scope.querySelectorAll('[data-dcc-booking]').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = '1';
      const phone = form.dataset.phone;
      const out = {
        pkg: form.querySelector('[data-out="pkg"]'),
        extras: form.querySelector('[data-out="extras"]'),
        vehicle: form.querySelector('[data-out="vehicle"]'),
        where: form.querySelector('[data-out="where"]'),
        date: form.querySelector('[data-out="date"]'),
        total: form.querySelector('[data-out="total"]'),
        totalNote: form.querySelector('[data-out="total-note"]'),
      };
      const dateInput = form.querySelector('input[name="date"]');
      const whereSel = form.querySelector('select[name="where"]');
      const addrWrap = form.querySelector('[data-addr]');
      const tomorrow = (() => { const t = new Date(); t.setDate(t.getDate() + 1); return t.toISOString().slice(0, 10); })();
      if (dateInput) dateInput.min = tomorrow;
      const euro = (n) => '€' + n.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      const fmtDate = (v) => {
        if (!v) return '';
        const d = new Date(v + 'T12:00:00');
        return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
      };
      const read = () => {
        const pkgEl = form.querySelector('input[name="pkg"]:checked');
        const vehEl = form.querySelector('input[name="vehicle"]:checked');
        const extras = [...form.querySelectorAll('input[name="extra"]:checked')];
        const whereOpt = whereSel?.selectedOptions[0] || null;
        const onRequest = vehEl?.dataset.onrequest === 'true';
        let total = 0;
        if (pkgEl) total += parseFloat(pkgEl.dataset.price || 0);
        extras.forEach((x) => { total += parseFloat(x.dataset.price || 0); });
        return { pkgEl, vehEl, extras, whereOpt, onRequest, total };
      };
      const render = () => {
        const s = read();
        // Mobile service / pickup: only from the configured start date, and ask for an address
        const from = s.whereOpt?.dataset.from || '';
        const away = s.whereOpt?.dataset.away === 'true';
        if (dateInput) {
          dateInput.min = from && from > tomorrow ? from : tomorrow;
          if (dateInput.value && dateInput.value < dateInput.min) dateInput.value = '';
        }
        if (addrWrap) addrWrap.hidden = !away;
        if (out.pkg) out.pkg.textContent = s.pkgEl ? s.pkgEl.dataset.label : '—';
        if (out.extras) out.extras.textContent = s.extras.length ? s.extras.map((x) => x.dataset.label).join(', ') : 'Geen';
        if (out.vehicle) out.vehicle.textContent = s.vehEl ? s.vehEl.dataset.label : '—';
        if (out.where) out.where.textContent = s.whereOpt ? s.whereOpt.dataset.short || s.whereOpt.textContent : '—';
        if (out.date) out.date.textContent = fmtDate(dateInput?.value) || 'In overleg';
        if (out.total) out.total.textContent = s.onRequest ? 'Op aanvraag' : (s.total ? euro(s.total) : '—');
        if (out.totalNote) out.totalNote.hidden = !s.onRequest;
      };
      const pre = new URLSearchParams(location.search).get('pakket');
      if (pre) { const el = form.querySelector(`input[name="pkg"][data-handle="${CSS.escape(pre)}"]`); if (el) el.checked = true; }
      form.addEventListener('change', render);
      form.addEventListener('input', render);
      render();
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const s = read();
        const f = new FormData(form);
        if (!s.pkgEl) {
          const first = form.querySelector('input[name="pkg"]');
          first?.closest('fieldset')?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
          first?.focus({ preventScroll: true });
          return;
        }
        const away = s.whereOpt?.dataset.away === 'true';
        const lines = [
          'Hoi Dutch Car Care! Ik wil graag een afspraak maken.',
          '',
          `Pakket: ${s.pkgEl.dataset.label}`,
          s.extras.length ? `Extra: ${s.extras.map((x) => x.dataset.label).join(', ')}` : null,
          s.vehEl ? `Voertuig: ${s.vehEl.dataset.label}` : null,
          f.get('car') ? `Auto: ${f.get('car')}` : null,
          s.whereOpt ? `Locatie: ${s.whereOpt.dataset.short || s.whereOpt.textContent}` : null,
          away && f.get('addr') ? `Adres: ${f.get('addr')}` : null,
          `Voorkeursdatum: ${fmtDate(f.get('date')) || 'in overleg'}${f.get('daypart') ? ' (' + f.get('daypart') + ')' : ''}`,
          f.get('name') ? `Naam: ${f.get('name')}` : null,
          f.get('notes') ? `Opmerking: ${f.get('notes')}` : null,
          '',
          s.onRequest ? 'Prijs: graag een prijs op aanvraag.' : `Indicatie: ${euro(s.total)}`,
        ].filter((l) => l !== null);
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(lines.join('\n'))}`;
        window.open(url, '_blank', 'noopener');
      });
    });
  }
  initBooking(document);

  // Year
  document.querySelectorAll('[data-year]').forEach((el) => { el.textContent = new Date().getFullYear(); });
})();